import { get } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, SubmissionError, Field, change } from 'redux-form';
import { renderTextField } from '../../../assets/formComponent';
import { firebaseSignup, firebaseSigninRedirect } from '../../../actions/auth';
import { load } from '../../../actions/common';
import { validateEmail, validatePassword } from '../../../assets/util';
import { getTranslate } from 'react-localize-redux';
import { Link } from 'react-router-dom';
import CheckboxComponent from '../../form/CheckboxComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { authControlState } from '../authControlState';
import SignupChallenge, { SIGNUP_RECAPTCHA_SITE_KEY } from '../SignupChallenge';

class SignupForm extends Component {

    constructor(props) {
        super(props);
        // Held in component state rather than redux-form: the challenge token is
        // single-use, expires on its own, and must never be persisted or replayed.
        this.state = { recaptchaToken: null };
        this.firebaseFbLogin = this.firebaseFbLogin.bind(this);
        this.firebaseGgLogin = this.firebaseGgLogin.bind(this);
        this.firebaseAppleLogin = this.firebaseAppleLogin.bind(this);
        this.onChallengeChange = this.onChallengeChange.bind(this);
    }

    /**
     * Stores the challenge token emitted by the widget.
     * @param {string|null} recaptchaToken - `null` when the challenge expires.
     * @returns {void}
     */
    onChallengeChange(recaptchaToken) {
        this.setState({ recaptchaToken });
    }

    onSubmit(values) {
        const errObj = { _error: "Submit failed" };
        let error = false;
        if (!values.first_name) {
            error = true;
            errObj.first_name = "Name is required";
        }
        if (!values.last_name) {
            error = true;
            errObj.last_name = "Lastname is required";
        }
        if (!values.email) {
            error = true;
            errObj.email = "Email is required";
        } else if (!validateEmail(values.email)) {
            error = true;
            errObj.email = "Email is invalid.";
        }
        if (!values.password) {
            error = true;
            errObj.password = "Password is required";
        } else if (!validatePassword(values.password)) {
            error = true;
            errObj.password = "Password needs to be more than 8 characters.";
        }

        // Only enforced client-side when a site key is configured, so with the
        // feature dark the form behaves exactly as before. The backend guard is
        // the real control; this check exists to save the user a round trip.
        if (SIGNUP_RECAPTCHA_SITE_KEY && !this.state.recaptchaToken) {
            error = true;
            errObj._error = this.props.translate('AUTH_CHALLENGE_REQUIRED');
        }

        if (error) {
            throw new SubmissionError(errObj);
        } else {
            this.props.firebaseSignup({
                email: values.email,
                password: values.password,
                first_name: values.first_name,
                last_name: values.last_name,
                email_consent: values.email_consent,
                recaptchaToken: this.state.recaptchaToken
            });
        }
    }

    firebaseFbLogin() {
        this.props.firebaseSigninRedirect("facebook");
    }

    firebaseGgLogin() {
        this.props.firebaseSigninRedirect("google");
    }

    firebaseAppleLogin() {
        this.props.firebaseSigninRedirect("apple");
    }

    render() {
        const { handleSubmit, signupErr, translate, q, authPending } = this.props;
        const email = authControlState(authPending, 'email');
        const facebook = authControlState(authPending, 'facebook');
        const google = authControlState(authPending, 'google');
        const apple = authControlState(authPending, 'apple');
        return (
            <div className="auth-form-container">
                <form className="auth-form" onSubmit={handleSubmit(this.onSubmit.bind(this))}>
                    {/* Error Alert */}
                    {(signupErr !== null || Boolean(get(q, "error", null))) && (
                        <div className="error-alert">
                            <div className="error-message">
                                <FontAwesomeIcon icon={['fas', 'exclamation-circle']} className="error-icon" />
                                {translate(`ERROR.AUTH.${get(signupErr, 'code', null)}`, get(signupErr, 'message', 'พบปัญหาขัดข้องบางประการ กรุณาติดต่อเจ้าหน้าที่ค่ะ'), { showMissingTranslationMsg: get(signupErr, 'message', 'พบปัญหาขัดข้องบางประการ กรุณาติดต่อเจ้าหน้าที่ค่ะ') }) || translate(`ERROR.${get(q, "error", '').toUpperCase()}`)}
                            </div>
                        </div>
                    )}

                    {/* Form Fields */}
                    <div className="form-fields">
                        <div className="name-fields">
                            <Field
                                component={renderTextField}
                                name="first_name"
                                label="ชื่อ"
                                type="text"
                                className="form-field first-name"
                            />
                            <Field
                                component={renderTextField}
                                name="last_name"
                                label="นามสกุล"
                                type="text"
                                className="form-field last-name"
                            />
                        </div>

                        <Field
                            component={renderTextField}
                            name="email"
                            label="Email"
                            type="text"
                            className="form-field"
                        />

                        <Field
                            component={renderTextField}
                            name="password"
                            label="Password"
                            type="password"
                            className="form-field"
                        />
                    </div>

                    {/* Consent Checkbox */}
                    <div className="consent-container">
                        <Field
                            component={CheckboxComponent}
                            name="email_consent"
                            label={
                                <span className="consent-text">
                                    ฉันต้องการรับสิทธิพิเศษ และ โปรโมชั่นรวมถึงข่าวสาร จาก บ.บิวเทรี่ยม และ พันธมิตร ตามที่ระบุไว้ใน{' '}
                                    <Link to="/privacy" className="consent-link">
                                        นโยบายความเป็นส่วนตัว
                                    </Link>
                                </span>
                            }
                        />
                    </div>

                    {/* Terms and Conditions */}
                    <div className="terms-container">
                        <p className="terms-text">
                            การสมัครสมาชิกนี้เป็นการยอมรับ{' '}
                            <Link to="/tos" className="terms-link">
                                ข้อกำหนดและเงื่อนไข
                            </Link>
                            {' '}และ รับทราบ{' '}
                            <Link to="/privacy" className="terms-link">
                                นโยบายความเป็นส่วนตัว
                            </Link>
                        </p>
                    </div>

                    {/* Bot-protection challenge. Renders nothing until
                        REACT_APP_SIGNUP_RECAPTCHA_SITE_KEY is configured. */}
                    <SignupChallenge onChange={this.onChallengeChange} />

                    {/* Submit Button */}
                    <button className="btn btn-primary submit-btn" type="submit" disabled={email.disabled}>
                        <FontAwesomeIcon icon={['fas', email.pending ? 'spinner' : 'user-plus']} className="btn-icon" spin={email.pending} />
                        {email.pending ? translate('AUTH_SIGNING_UP') : 'สมัครสมาชิก'}
                    </button>

                    {/* Divider */}
                    <div className="divider">
                        <span className="divider-text">หรือ</span>
                    </div>

                    {/* Social Login Buttons */}
                    <div className="social-buttons">
                        <button
                            className="social-btn facebook-btn"
                            type="button"
                            onClick={this.firebaseFbLogin}
                            disabled={facebook.disabled}
                        >
                            <FontAwesomeIcon icon={facebook.pending ? ['fas', 'spinner'] : ['fab', 'facebook-f']} className="social-icon" spin={facebook.pending} />
                            {facebook.pending ? translate('AUTH_SIGNING_IN') : 'สมัครสมาชิกด้วย Facebook'}
                        </button>

                        <button
                            className="social-btn google-btn"
                            type="button"
                            onClick={this.firebaseGgLogin}
                            disabled={google.disabled}
                        >
                            <FontAwesomeIcon icon={google.pending ? ['fas', 'spinner'] : ['fab', 'google']} className="social-icon" spin={google.pending} />
                            {google.pending ? translate('AUTH_SIGNING_IN') : 'สมัครสมาชิกด้วย Google'}
                        </button>

                        <button
                            className="social-btn apple-btn"
                            type="button"
                            onClick={this.firebaseAppleLogin}
                            disabled={apple.disabled}
                        >
                            <FontAwesomeIcon icon={apple.pending ? ['fas', 'spinner'] : ['fab', 'apple']} className="social-icon" spin={apple.pending} />
                            {apple.pending ? translate('AUTH_SIGNING_IN') : 'สมัครสมาชิกด้วย Apple'}
                        </button>
                    </div>
                </form>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        signupResult: state.auth.signupResult,
        signupErr: state.auth.signupErr,
        authenticated: state.auth.authenticated,
        authError: state.auth.error,
        fbResponse: state.misc.fbResponse,
        authPending: state.common.authPending,
        translate: getTranslate(state.locale),
    }
}

export default reduxForm({
    form: 'popupSignupForm'
})(connect(mapStateToProps, { change, load, firebaseSignup, firebaseSigninRedirect })(SignupForm));